'use memo'

import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { isConfirmedRegistrationSession } from '@sportninja/common/features/registration/teamBudgetPayment/utils/confirmedSession'
import { withFreshSession } from '../utils/freshRegistration'
import { withoutSelectionParams } from '../utils/selectionParams'
import { useStartOver } from './useStartOver'

/** The fields of a held session this hook needs. */
interface HeldSession {
  id: string
  registration_step?: string | null
}

interface Params {
  /** The context's `resetSession`, which deletes a session by id. */
  resetSession: (sessionId?: string) => Promise<void>
  /** Opens the wizard at a URL; the default loads it in this tab. */
  navigate?: (url: string) => void
}

/**
 * The running-session prompt: the session it holds, and its two answers.
 *
 * `index.jsx` holds a resumed session here, instead of applying it, when a
 * `?team_id=` link conflicts with it (`sessionLinkConflict`). Nothing renders
 * underneath while it is held, so the start step cannot auto-advance on a
 * link the reader has not agreed to.
 */
export const useSessionLinkConflict = ({
  resetSession,
  navigate = (url) => window.location.assign(url),
}: Params) => {
  const history = useHistory()
  const [heldSession, setHeldSession] = useState<HeldSession | null>(null)
  const { startOver: deleteHeldAndReload, isStartingOver } = useStartOver({
    resetSession,
    sessionId: heldSession?.id,
  })

  /**
   * Decline the link: drop it from the URL and let the ordinary resume run.
   *
   * Clearing the held session re-runs `index.jsx`'s session effect, which
   * fetches the same stored session again — now with no link to conflict
   * with — and routes to its own step. One extra request, in exchange for
   * reusing the resume path rather than duplicating it here.
   */
  const cancel = () => {
    // The modal's X and Esc still reach here while the delete is in flight;
    // the reader has already chosen to start over.
    if (isStartingOver) {
      return
    }
    history.replace({
      search: withoutSelectionParams(history.location.search),
    })
    setHeldSession(null)
  }

  /**
   * Accept the link: delete the held session and reload with the link intact.
   *
   * The reload finds the stored id gone and creates a fresh session on the
   * start step, which takes the link as a new arrival. A session the server
   * refuses to delete (SN-8441 — a Team Budget payment was taken for it) is kept,
   * and the reader is told why; the prompt stays.
   *
   * A registration already completed is never deleted: it is kept, and the link
   * opens on a new session — as "New Registration" does on the confirmation step.
   */
  const startOver = async () => {
    if (!heldSession) {
      return
    }
    if (isConfirmedRegistrationSession(heldSession)) {
      navigate(
        `${window.location.pathname}${withFreshSession(window.location.search)}`
      )
      return
    }
    await deleteHeldAndReload()
  }

  return {
    heldSession,
    holdSession: setHeldSession,
    cancel,
    startOver,
    isStartingOver,
  }
}
